import React from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useToast } from "@/shared/hooks/use-toast";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { MultiSelect } from "@/shared/components/ui/multi-select";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { InviteService, CreateInviteDTO } from "@/features/Users/services/inviteService";
import { OrganizationService } from "@/features/Organization/services/organizationService";
import { analyticsService } from "@/shared/services/analytics";
import { useIsSuperAdminRole } from "@/shared/hooks/use-system-role";
import { useSuperAdminOrg } from "@/shared/contexts/SuperAdminOrgContext";

interface Workspace {
  id: string;
  name: string;
  description?: string;
  logo_url?: string;
}

const createInviteFormSchema = (t: TFunction) => z.object({
  full_name: z.string().min(1, t('users.validation.nameRequired')),
  email: z.string().email(t('users.validation.emailInvalid')),
  role: z.enum(["User", "Admin", "Super Admin"], {
    required_error: t('users.validation.roleRequired'),
  }),
  product_ids: z.array(z.string()).optional(),
});

type InviteUserDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  products: Workspace[];
  organizationId?: string;
  preselectedProductId?: string; // Produto pré-selecionado (vindo do WorkspaceDialog)
};

export const InviteUserDialog = ({ open, onOpenChange, products, organizationId, preselectedProductId }: InviteUserDialogProps) => {
  const { t, i18n } = useTranslation();
  const inviteFormSchema = createInviteFormSchema(t);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isSuperAdmin: currentUserIsSuperAdmin } = useIsSuperAdminRole();
  const { selectedOrganization } = useSuperAdminOrg();

  // Estado para armazenar o ID da organização obtido do OrganizationService
  const [organizationIdFromService, setOrganizationIdFromService] = React.useState<string | undefined>(undefined);
  
  // Obter o ID da organização do OrganizationService
  React.useEffect(() => {
    const fetchOrganizationId = async () => {
      try {
        // Usar o OrganizationService para obter o perfil do usuário que contém o ID da organização
        const userProfile = await OrganizationService.getCurrentUserProfile();
        if (userProfile && userProfile.organization_id) {
          setOrganizationIdFromService(userProfile.organization_id);
        }
      } catch (error) {
        console.error('[InviteUserDialog] Erro ao obter o perfil do usuário:', error);
      }
    };
    
    fetchOrganizationId();
  }, []);
  

  const form = useForm<z.infer<typeof inviteFormSchema>>({
    resolver: zodResolver(inviteFormSchema),
    defaultValues: {
      full_name: "",
      email: "",
      role: "User",
      product_ids: preselectedProductId ? [preselectedProductId] : [],
    },
  });

  // Atualizar product_ids quando preselectedProductId mudar
  React.useEffect(() => {
    if (preselectedProductId && open) {
      form.setValue('product_ids', [preselectedProductId]);
    }
  }, [preselectedProductId, open, form]);

  const createInviteMutation = useMutation({
    mutationFn: async (values: z.infer<typeof inviteFormSchema>) => {
      try {
        // Normalizar o email para minúsculas
        const normalizedEmail = values.email.toLowerCase();
        
        // Definir o organization_id a ser usado no convite
        // 1. Superadmin: usar org selecionada no contexto
        // 2. Prop fornecida pelo componente pai
        // 3. Fallback: org do perfil do usuário
        const effectiveOrgId = (currentUserIsSuperAdmin && selectedOrganization?.id)
          ? selectedOrganization.id
          : (organizationId || organizationIdFromService);
        
        if (!effectiveOrgId) {
          console.error('ERRO: ID da organização não encontrado');
          throw new Error('organization_id_missing');
        }
        
        // Verificar se o usuário já existe no sistema
        try {
          // Primeiro verificamos se já existe um convite pendente com este email
          const existingInvites = await InviteService.listInvites({
            email: normalizedEmail,
            organization_id: effectiveOrgId
          });
          
          if (existingInvites && existingInvites.length > 0) {
            console.warn(`[InviteUserDialog] Já existe um convite para o email ${normalizedEmail}`);
            throw new Error('duplicate_invite');
          }
          
          // Também podemos verificar se o usuário já existe nos profiles
          // Esta verificação seria feita no backend, mas podemos adicionar aqui também
        } catch (checkError) {
          // Se o erro for de duplicidade, repassamos
          if (checkError instanceof Error && checkError.message === 'duplicate_invite') {
            throw checkError;
          }
          // Outros erros na verificação podem ser ignorados para continuar com a tentativa de criar o convite
          console.warn('Erro ao verificar convites existentes:', checkError);
        }
        
        // Criar o convite incluindo explicitamente o organization_id
        // Garantir que product_ids seja enviado no formato correto para o tipo JSONB do banco
        const inviteData: CreateInviteDTO = {
          email: normalizedEmail,
          user_name: values.full_name,
          role: values.role === "Super Admin" ? "superadmin" : values.role.toLowerCase(),
          // Garantir que product_ids seja um array mesmo que seja vazio
          product_ids: Array.isArray(values.product_ids) ? values.product_ids : [],
          // Sempre incluir o organization_id de forma explícita
          organization_id: effectiveOrgId,
          // Idioma em uso por quem convida: sem isto o backend assume inglês e o
          // convite sai em inglês mesmo com a plataforma em português (AB#84509).
          locale: i18n.language,
        };
        
        // Log para depuração com informações mais detalhadas
        
        // Criar o convite com verificação adicional
        try {
          // Usar o InviteService para criar o convite
          // O service vai escolher o endpoint correto com base na presença do organization_id
          const result = await InviteService.createInvite(inviteData);
          return result;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
          console.error('Erro detalhado ao criar convite:', error);
          
          // Verificar se a mensagem indica um convite duplicado
          if (error.message?.includes('409') || 
              error.message?.includes('duplicate') || 
              error.message?.includes('already exists') || 
              error.message?.includes('já existe')) {
            throw new Error('duplicate_invite');
          }
          
          // Verificar se é um erro de autenticação ou autorização
          if (error.message?.includes('401') || error.message?.includes('403')) {
            throw new Error('auth_error');
          }
          
          throw error;
        }
      } catch (error) {
        console.error('Erro ao criar convite:', error);
        
        // Relançar o erro para ser tratado pelo onError
        throw error;
      }
    },
    onSuccess: (_data, variables) => {
      // Invalidar todas as queries relacionadas a convites para garantir atualização da lista
      queryClient.invalidateQueries({ queryKey: ['invites'] });
      
      // Invalidar queries de convites pendentes do produto
      queryClient.invalidateQueries({ queryKey: ['workspace-pending-invites'] });
      queryClient.invalidateQueries({ predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'workspace-invites' });
      
      // Forçar o refetch imediato para garantir que a lista seja atualizada
      queryClient.refetchQueries({ 
        queryKey: ['invites'],
        exact: false
      });
      
      queryClient.refetchQueries({ 
        queryKey: ['workspace-pending-invites'],
        exact: false
      });
      
      toast({
        title: t("settings.users.inviteSuccess"),
        description: t("settings.users.inviteSuccessDescription"),
      });

      // Track user invite
      analyticsService.trackEvent('user_invited', {
        role: variables.role,
        product_count: variables.product_ids?.length || 0,
        invited_by: user?.email || 'unknown'
      });
      
      form.reset();
      onOpenChange(false);
    },
         // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any, variables) => {
      console.error('Erro ao criar convite:', error);
      
      // Track invite failure
      analyticsService.trackEvent('user_invite_failed', {
        email: variables.email,
        error_message: error.message || 'unknown_error'
      });
      
      // Verificar tipos específicos de erro
      if (error.message === 'user_in_another_org') {
        toast({
          variant: "destructive",
          title: t("settings.users.inviteError"),
          description: t("settings.users.userInAnotherOrgError"),
        });
        return;
      }

      if (error.message === 'duplicate_invite') {
        toast({
          variant: "destructive",
          title: t("settings.users.inviteError"),
          description: t("settings.users.duplicateInviteError"),
        });
      } else if (error.message?.includes('assinatura') || error.message?.includes('subscription')) {
        // Erro de assinatura - exibir mensagem da API
        toast({
          variant: "destructive",
          title: t("settings.users.inviteError"),
          description: resolveApiErrorMessage(error, t),
        });
      } else {
        // Outros erros - mensagem amigável/localizada (code → status → genérica)
        toast({
          variant: "destructive",
          title: t("settings.users.inviteError"),
          description: resolveApiErrorMessage(error, t),
        });
      }
    },
  });

  const onSubmit = async (values: z.infer<typeof inviteFormSchema>) => {
    try {
      await createInviteMutation.mutateAsync(values);
    } catch (error) {
      console.error('Error in onSubmit:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("settings.users.inviteTitle")}</DialogTitle>
          <DialogDescription>
            {t("settings.users.inviteDescription")}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="full_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("settings.users.fields.name")}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("settings.users.fields.email")}</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("settings.users.fields.role")}</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("settings.users.selectRole")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="User">{t("settings.users.roles.user")}</SelectItem>
                      <SelectItem value="Admin">{t("settings.users.roles.admin")}</SelectItem>
                      {currentUserIsSuperAdmin && (
                        <SelectItem value="Super Admin">{t("settings.users.roles.superadmin")}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {!preselectedProductId && form.watch('role') !== 'Super Admin' && (
              <FormField
                control={form.control}
                name="product_ids"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("settings.users.fields.workspaces")}</FormLabel>
                    <FormControl>
                      <MultiSelect
                        options={products.map(workspace => ({
                          value: workspace.id,
                          label: workspace.name,
                          logoUrl: workspace.logo_url,
                          description: workspace.description
                        }))}
                        placeholder={t("settings.users.selectWorkspaces")}
                        selected={field.value || []}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <DialogFooter>
              <Button type="submit" disabled={createInviteMutation.isPending}>
                {createInviteMutation.isPending ? t("common.saving") : t("settings.users.sendInvite")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
